import { Temporal } from '@js-temporal/polyfill';

// CB-REQ VEN-01 at v1.9104: the venue page reads by the calendar week, Monday to Sunday in the
// venue's own zone (INV-02), and by rolling spans from now. Instants in, so a week with a daylight
// saving change is the 167 or 169 hours it has, not seven times 24.

export interface Week {
  /** Monday, the first local date. */
  readonly monday: string;
  readonly days: readonly string[];
  readonly from: Temporal.Instant;
  readonly to: Temporal.Instant;
}

/** The Monday-to-Sunday week holding `date`, moved by `offset` weeks, in `zone`. */
export function weekOf(date: string, zone: string, offset = 0): Week {
  const day = Temporal.PlainDate.from(date);
  const monday = day.subtract({ days: day.dayOfWeek - 1 }).add({ weeks: offset });
  const at = (d: Temporal.PlainDate) => d.toZonedDateTime({ timeZone: zone }).toInstant();
  return {
    monday: monday.toString(),
    days: Array.from({ length: 7 }, (_, i) => monday.add({ days: i }).toString()),
    from: at(monday),
    to: at(monday.add({ days: 7 })),
  };
}

/**
 * [now − days, now) or [now, now + days), as instants, from the start of the current hour. Reads
 * are keyed on it, so it must not move between renders: to the millisecond, every render asked
 * again (5,013 reads of a venue's jobs in 4 seconds, found 2026-10-27).
 */
export function span(now: Temporal.Instant, days: number): { from: string; to: string } {
  const hour = now.round({ smallestUnit: 'hour', roundingMode: 'floor' });
  const other = hour.add({ hours: 24 * Math.abs(days) * Math.sign(days) });
  const [a, b] = days < 0 ? [other, hour] : [hour, other];
  return { from: a.toString(), to: b.toString() };
}

/** What the week by room needs of a job and a quote (V-1's reads). */
export interface WeekJob {
  readonly id: string;
  readonly reference: string;
  readonly name: string;
  readonly scheduledWindow: { readonly startsAt: string; readonly endsAt: string };
  readonly rooms: ReadonlyArray<{ readonly id: string; readonly short?: boolean | null }>;
  readonly venueGearOnly: boolean;
}
export interface WeekQuote {
  readonly id: string;
  readonly reference: string;
  readonly pencilled: boolean;
  readonly scheduledWindow: { readonly startsAt: string; readonly endsAt: string };
  readonly rooms: ReadonlyArray<{ readonly id: string }>;
}

export type Booking =
  | { readonly kind: 'ours' | 'short' | 'venue'; readonly job: WeekJob }
  | { readonly kind: 'pencil'; readonly quote: WeekQuote };

/** The key for bookings in no room: a job or quote at the venue with no line in a room. */
export const WHOLE_VENUE = 'whole-venue';

/**
 * The week by room: for each room (and the whole venue), each day's bookings. A job fills every
 * day its scheduled window touches in each room its lines use; short where a line there is short,
 * grey where it is only the venue's own gear; a pencilled quote dashed.
 */
export function weekGrid(
  week: Week,
  zone: string,
  jobs: readonly WeekJob[],
  quotes: readonly WeekQuote[],
): Map<string, Booking[][]> {
  const grid = new Map<string, Booking[][]>();
  const cell = (room: string, day: number) => {
    const row = grid.get(room) ?? week.days.map(() => []);
    grid.set(room, row);
    return row[day]!;
  };
  const dayBounds = week.days.map((d) => {
    const start = Temporal.PlainDate.from(d).toZonedDateTime({ timeZone: zone }).toInstant();
    const end = Temporal.PlainDate.from(d)
      .add({ days: 1 })
      .toZonedDateTime({ timeZone: zone })
      .toInstant();
    return [start, end] as const;
  });
  const touches = (w: { startsAt: string; endsAt: string }, i: number) => {
    const [start, end] = dayBounds[i]!;
    return (
      Temporal.Instant.compare(Temporal.Instant.from(w.startsAt), end) < 0 &&
      Temporal.Instant.compare(Temporal.Instant.from(w.endsAt), start) > 0
    );
  };
  for (const job of jobs) {
    const rooms = job.rooms.length > 0 ? job.rooms : [{ id: WHOLE_VENUE, short: false }];
    for (const r of rooms) {
      const kind = job.venueGearOnly ? 'venue' : r.short ? 'short' : 'ours';
      week.days.forEach((_, i) => {
        if (touches(job.scheduledWindow, i)) cell(r.id, i).push({ kind, job });
      });
    }
  }
  for (const quote of quotes.filter((q) => q.pencilled)) {
    const rooms = quote.rooms.length > 0 ? quote.rooms : [{ id: WHOLE_VENUE }];
    for (const r of rooms) {
      week.days.forEach((_, i) => {
        if (touches(quote.scheduledWindow, i)) cell(r.id, i).push({ kind: 'pencil', quote });
      });
    }
  }
  return grid;
}

/** What movements need of a job. */
export interface MovingJob extends WeekJob {
  readonly lines: number;
  readonly outAt?: string | null;
  readonly backAt?: string | null;
  readonly dockBooking?: { readonly reference: string } | null;
}

export interface Movement {
  readonly way: 'in' | 'out';
  readonly at: string;
  /** Done (from the book-out or check-in), or as scheduled. */
  readonly done: boolean;
  readonly job: MovingJob;
}

/**
 * Gear in and out for the jobs at the venue, inside the week: in when it went out to the venue
 * (WHS-16's book-out) or is due to, out when it came back or is due to, soonest first.
 */
export function movements(week: Week, jobs: readonly MovingJob[]): Movement[] {
  const inside = (at: string) => {
    const i = Temporal.Instant.from(at);
    return Temporal.Instant.compare(i, week.from) >= 0 && Temporal.Instant.compare(i, week.to) < 0;
  };
  const out: Movement[] = [];
  for (const job of jobs) {
    const arrive = job.outAt ?? job.scheduledWindow.startsAt;
    const leave = job.backAt ?? job.scheduledWindow.endsAt;
    if (inside(arrive)) out.push({ way: 'in', at: arrive, done: !!job.outAt, job });
    if (inside(leave)) out.push({ way: 'out', at: leave, done: !!job.backAt, job });
  }
  return out.sort((a, b) =>
    Temporal.Instant.compare(Temporal.Instant.from(a.at), Temporal.Instant.from(b.at)),
  );
}
